import type { BackgroundTheme, BrandColors, HSLString } from '@/types/color';
import type { TenantOption } from '@/types/tenant';
import { getTenantConfiguration } from '@/utils/tenant';

export const TURNIT_BRAND_COLORS: BrandColors = {
  dark: '#010845',
  primary: '#003FBF',
  cta: '#003FBF',
  ctaContrast: '#FFFFFF',
  tint: '#8619FF',
  accent: '#FFF250',
};

export const ARENAWAYS_BRAND_COLORS: BrandColors = {
  dark: '#040065',
  primary: '#17136C',
  cta: '#FFBA2A',
  ctaContrast: '#040065',
  tint: '#005EC2',
  accent: '#F5A501',
};

export const TWILINER_BRAND_COLORS: BrandColors = {
  dark: '#27205B',
  primary: '#905090',
  cta: '#FFA970',
  ctaContrast: '#17114B',
  tint: '#46288C',
  accent: '#FFC973',
};

export const VELVET_BRAND_COLORS: BrandColors = {
  dark: '#06383A',
  primary: '#9C43B2',
  cta: '#F0AAFF',
  ctaContrast: '#003E40',
  tint: '#003E40',
  accent: '#F0AAFF',
};

export const AGUIA_BRAND_COLORS: BrandColors = {
  dark: '#071270',
  primary: '#0064CB',
  cta: '#0D6EFD',
  ctaContrast: '#ffffff',
  tint: '#348452',
  accent: '#00E5CE',
};

export const setGlobalStyleProperties = (
  styleProperties: Record<string, string>,
  backgroundTheme: BackgroundTheme
) => {
  Object.entries(styleProperties).forEach(([key, value]) => {
    document.documentElement.style.setProperty(`--${key}`, value);
  });

  if (isLightBackground(backgroundTheme)) {
    document.body.classList.remove('text-white');
    document.body.classList.add('text-dark');
  } else {
    document.body.classList.remove('text-dark');
    document.body.classList.add('text-white');
  }
};

export const hexToHSL = (hex: string): HSLString => {
  // Convert hex to RGB
  const r: number = parseInt(hex.slice(1, 3), 16) / 255;
  const g: number = parseInt(hex.slice(3, 5), 16) / 255;
  const b: number = parseInt(hex.slice(5, 7), 16) / 255;

  // Find min and max RGB components
  const cmin: number = Math.min(r, g, b);
  const cmax: number = Math.max(r, g, b);
  const delta: number = cmax - cmin;

  let h: number, s: number, l: number;

  // Calculate hue
  if (delta === 0) h = 0;
  else if (cmax === r) h = ((g - b) / delta) % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  h = Math.round(h * 60);
  if (h < 0) h += 360;

  // Calculate lightness
  l = (cmax + cmin) / 2;

  // Calculate saturation
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  // Convert to percentages
  s = s * 100;
  l = l * 100;

  return `${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%`;
};

export const hslToHex = (hsl: string): string => {
  const [h, s, l] = hsl
    .split(',')
    .map((value, index) =>
      index === 0 ? parseFloat(value) : parseFloat(value) / 100
    );

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  const toHex = (value: number) => {
    const hex = Math.round((value + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const decodeBase64SVG = (
  base64: string,
  background: HSLString,
  outline: HSLString
) => {
  const decoded = atob(base64);

  let count = 0;

  const updatedSvgString = decoded.replace(/fill="[^"]*"/g, () => {
    let newFill;

    if (count === 0) {
      newFill = 'fill="none"';
    } else if (count === 1) {
      newFill = `fill="${hslToHex(background)}"`;
    } else {
      newFill = `fill="${hslToHex(outline)}"`;
    }

    count++;
    return newFill;
  });

  return btoa(updatedSvgString);
};

export const createBrandColorTint = (
  hex: string,
  lightnessChange: number,
  lightnessMode: 'add' | 'subtract' | 'set' | 'keep',
  saturationChange: number,
  saturationMode: 'add' | 'subtract' | 'set' | 'keep'
): HSLString => {
  const hslString = hexToHSL(hex);
  const [h, s, l] = hslString.match(/\d+/g)!.map(Number);
  let newColorSaturation: number = s;
  let newColorLightness: number = l;

  switch (saturationMode) {
    case 'add':
      newColorSaturation = Math.min(100, s + saturationChange);
      break;
    case 'subtract':
      newColorSaturation = Math.max(0, s - saturationChange);
      break;
    case 'set':
      newColorSaturation = Math.max(0, Math.min(100, saturationChange));
      break;
    case 'keep':
    default:
      break;
  }

  switch (lightnessMode) {
    case 'add':
      newColorLightness = Math.min(100, l + lightnessChange);
      break;
    case 'subtract':
      newColorLightness = Math.max(0, l - lightnessChange);
      break;
    case 'set':
      newColorLightness = Math.max(0, Math.min(100, lightnessChange));
      break;
    case 'keep':
    default:
      break;
  }

  return `${Math.round(h)}, ${Math.round(newColorSaturation)}%, ${Math.round(newColorLightness)}%`;
};

export const getCalculatedStyleProperties = (
  brandColors: BrandColors
): Record<string, HSLString> => ({
  primary: hexToHSL(brandColors.primary),
  'primary-dark': createBrandColorTint(
    brandColors.primary,
    15,
    'subtract',
    0,
    'keep'
  ),
  'primary-medium': createBrandColorTint(
    brandColors.primary,
    7,
    'subtract',
    5,
    'add'
  ),
  'primary-light': createBrandColorTint(
    brandColors.primary,
    95,
    'set',
    0,
    'keep'
  ),
  'primary-pale': createBrandColorTint(
    brandColors.primary,
    97,
    'set',
    0,
    'keep'
  ),

  cta: hexToHSL(brandColors.cta),
  'cta-contrast': hexToHSL(brandColors.ctaContrast),
  'cta-dark': createBrandColorTint(brandColors.cta, 15, 'subtract', 0, 'keep'),
  'cta-medium': createBrandColorTint(brandColors.cta, 7, 'subtract', 5, 'add'),
  'cta-light': createBrandColorTint(brandColors.cta, 95, 'set', 0, 'keep'),
  'cta-pale': createBrandColorTint(brandColors.cta, 97, 'set', 0, 'keep'),

  tint: hexToHSL(brandColors.tint),
  'tint-light': createBrandColorTint(brandColors.tint, 95, 'set', 0, 'keep'),
  'tint-pale': createBrandColorTint(brandColors.tint, 97, 'set', 0, 'keep'),

  accent: hexToHSL(brandColors.accent),
  'accent-light': createBrandColorTint(
    brandColors.accent,
    73,
    'set',
    0,
    'keep'
  ),
  'accent-pale': createBrandColorTint(brandColors.accent, 83, 'set', 0, 'keep'),

  neutral: createBrandColorTint(brandColors.dark, 45, 'set', 75, 'subtract'),
  'neutral-medium': createBrandColorTint(
    brandColors.dark,
    60,
    'set',
    80,
    'subtract'
  ),
  'neutral-light': createBrandColorTint(
    brandColors.dark,
    80,
    'set',
    80,
    'subtract'
  ),
  'neutral-pale': createBrandColorTint(
    brandColors.dark,
    93,
    'set',
    75,
    'subtract'
  ),

  'greyscale-800': hexToHSL('#474747'),
  'greyscale-100': hexToHSL('#F9F9F9'),

  'success-border': hexToHSL('#008E4C'),
  'success-text': hexToHSL('#007941'),
  'success-surface': hexToHSL('#EBF6F1'),
  'success-surface-hover': hexToHSL('#E0F1EA'),

  'warning-border': hexToHSL('#E57A00'),
  'warning-surface': hexToHSL('#FDF4EB'),
  'warning-surface-hover': hexToHSL('#FCEFE0'),

  'error-border': hexToHSL('#E00004'),
  'error-text': hexToHSL('#BE0003'),
  'error-surface': hexToHSL('#FDEBEB'),
  'error-surface-hover': hexToHSL('#FBE0E1'),

  'info-border': hexToHSL('#006DCC'),
  'info-text': hexToHSL('#005DAD'),
  'info-surface': hexToHSL('#EBF3FB'),
  'info-surface-hover': hexToHSL('#E0EDF9'),

  white: hexToHSL('#FFFFFF'),
  dark: hexToHSL(brandColors.dark),
});

export const getBackgroundColor = (
  backgroundTheme: BackgroundTheme,
  appColors: Record<string, HSLString>
): HSLString => {
  switch (backgroundTheme) {
    case 'dark':
      return appColors.dark;
    case 'pale':
      return appColors['neutral-pale'];
    case 'light':
      return appColors['primary-light'];
    case 'tint':
      return appColors['tint-light'];
    case 'white':
    default:
      return hexToHSL('#FFFFFF');
  }
};

export const isLightBackground = (
  backgroundTheme: BackgroundTheme
): boolean => {
  switch (backgroundTheme) {
    case 'dark':
      return false;
    case 'white':
    case 'pale':
    case 'light':
    case 'tint':
    default:
      return true;
  }
};

export const getTenantBrand = (
  tenant: TenantOption
): { brandColors: BrandColors; backgroundTheme: BackgroundTheme } => {
  const tenantConfiguration = getTenantConfiguration(tenant.value);

  return (
    tenantConfiguration ?? {
      brandColors: TURNIT_BRAND_COLORS,
      backgroundTheme: 'dark',
    }
  );
};

export const setTenantBrand = (tenant: TenantOption) => {
  const { brandColors, backgroundTheme } = getTenantBrand(tenant);

  const calculatedStyleProperties = getCalculatedStyleProperties(brandColors);
  const newBackgroundColor = getBackgroundColor(
    backgroundTheme,
    calculatedStyleProperties
  );

  setGlobalStyleProperties(
    {
      ...calculatedStyleProperties,
      background: newBackgroundColor,
      radius: '8px',
    },
    backgroundTheme
  );
};

export const setTenantFavicon = (tenant: TenantOption) => {
  const newFavicon = document.createElement('link');

  newFavicon.rel = 'icon';
  newFavicon.type = 'image/svg+xml';
  newFavicon.href = `/${tenant.value}.png`;

  document.head.appendChild(newFavicon);
};
